// Lib
import { useEffect, useReducer } from 'react';
import { nanoid } from 'nanoid';
import { first } from 'lodash/fp';

// State
import aiToolStateReducer from './aiToolStateReducer';
import {
    getCurrentConversation,
    getCurrentConversationChosenPromptShortcuts,
    getFirstConversation,
    isEmptyNewConversation,
} from './aiToolStateSelector';

// Types
import { PromptShortcut } from '../../../../../../../common/ai/aiPromptShortcutTypes';
import {
    AiToolState,
    AiToolActionType,
    AiPromptMode,
    AiPopupTransitionDirection,
    AiResultType,
} from './aiToolStateTypes';
import { AiConversation, AiDebugInfo } from '../../../../../../../common/ai/aiConversationTypes';

const initialState: AiToolState = {
    currentConversationId: '',
    conversations: {},
    mode: AiPromptMode.PROMPTS,
    returnMode: AiPromptMode.PROMPTS,
    transitionDirection: AiPopupTransitionDirection.UP,
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useAiToolStateReducer = (rootPromptShortcut: PromptShortcut | undefined) => {
    const [state, dispatch] = useReducer(aiToolStateReducer, initialState);

    const setMode = (mode: AiPromptMode) => {
        dispatch({
            type: AiToolActionType.SET_MODE,
            payload: mode,
        });
    };

    const setReturnMode = (mode: AiPromptMode) => {
        dispatch({
            type: AiToolActionType.SET_RETURN_MODE,
            payload: mode,
        });
    };

    const setTransitionDirection = (transitionDirection: AiPopupTransitionDirection) => {
        dispatch({
            type: AiToolActionType.SET_TRANSITION_DIRECTION,
            payload: transitionDirection,
        });
    };

    const setCurrentConversationId = (conversationId: string) => {
        dispatch({
            type: AiToolActionType.SET_CURRENT_CONVERSATION_ID,
            payload: conversationId,
        });
    };

    const setConversationFetching = (conversationId: string, fetching: boolean) => {
        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    fetching,
                },
            },
        });
    };

    const setConversationError = (conversationId: string, error: string | null) => {
        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    error,
                },
            },
        });
    };

    const setConversationTitle = (conversationId: string, title: string) => {
        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    title,
                },
            },
        });
    };

    const setConversationStreamingResults = (conversationId: string, streamingResults: boolean) => {
        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    streamingResults,
                },
            },
        });
    };

    const loadConversation = (conversationId: string, conversation: AiConversation) => {
        dispatch({
            type: AiToolActionType.LOAD_CONVERSATION,
            payload: {
                conversationId,
                conversation,
            },
        });
    };

    const loadConversationStubs = (conversations: Partial<AiConversation>[]) => {
        dispatch({
            type: AiToolActionType.LOAD_CONVERSATION_STUBS,
            payload: {
                conversations,
            },
        });
    };

    const createConversation = (conversationId: string) => {
        dispatch({
            type: AiToolActionType.CREATE_CONVERSATION,
            payload: {
                conversationId,
                rootPromptShortcut,
            },
        });
    };

    const appendResult = (conversationId: string, result: AiResultType) => {
        dispatch({
            type: AiToolActionType.ADD_CONVERSATION_RESULTS,
            payload: {
                conversationId,
                results: [result],
            },
        });
    };

    const appendPrompt = (conversationId: string, prompt: string) => {
        dispatch({
            type: AiToolActionType.ADD_CONVERSATION_RESULTS,
            payload: {
                conversationId,
                results: [
                    {
                        uuid: nanoid(),
                        elementType: 'PROMPT_HISTORY',
                        text: prompt,
                    },
                ],
            },
        });
    };

    const removeResult = (conversationId: string, resultUuid: string) => {
        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    results: state.conversations[conversationId]?.results.filter(
                        (result) => result.uuid !== resultUuid,
                    ),
                },
            },
        });
    };

    const appendDebugInfo = (conversationId: string, debugInfo: AiDebugInfo['debugInfo']) => {
        dispatch({
            type: AiToolActionType.ADD_CONVERSATION_RESULTS,
            payload: {
                conversationId,
                results: [
                    {
                        uuid: nanoid(),
                        elementType: 'DEBUG_INFO',
                        debugInfo,
                    },
                ],
            },
        });
    };

    // when rootPromptShortcut becomes available, create a new conversation
    useEffect(() => {
        if (rootPromptShortcut && state.currentConversationId === '') {
            const conversationId = nanoid();

            createConversation(conversationId);
            setCurrentConversationId(conversationId);
        }
    }, [rootPromptShortcut]);

    const addChosenPromptShortcut = (conversationId: string, promptShortcut: PromptShortcut) => {
        const selectedPromptShortcuts = state.conversations[conversationId]?.selectedPromptShortcuts;

        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    selectedPromptShortcuts: [...selectedPromptShortcuts, promptShortcut],
                },
            },
        });
    };

    const removeChosenPromptShortcuts = (conversationId: string) => {
        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    selectedPromptShortcuts: [],
                    previousSelectedPromptShortcuts: state.conversations[conversationId]?.selectedPromptShortcuts,
                },
            },
        });
    };

    const resetToPreviousPromptShortcuts = (conversationId: string) => {
        const previousSelectedPromptShortcut = first(
            state.conversations[conversationId]?.previousSelectedPromptShortcuts,
        );

        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    selectedPromptShortcuts: previousSelectedPromptShortcut ? [previousSelectedPromptShortcut] : [],
                    previousSelectedPromptShortcuts: [],
                },
            },
        });
    };

    const setPromptFollowupsStreaming = (conversationId: string, streamingFollowupPrompts: boolean) => {
        dispatch({
            type: AiToolActionType.UPDATE_CONVERSATION,
            payload: {
                conversationId,
                updates: {
                    streamingFollowupPrompts,
                },
            },
        });
    };

    const setAdditionalPromptShortcuts = (conversationId: string, promptShortcuts: PromptShortcut[]) => {
        dispatch({
            type: AiToolActionType.SET_CONVERSATION_PROMPT_SHORTCUTS,
            payload: {
                conversationId,
                promptShortcuts,
            },
        });
    };

    const resetPromptShortcut = (conversationId: string, rootPromptShortcut: PromptShortcut | undefined) => {
        dispatch({
            type: AiToolActionType.RESET_CONVERSATION_PROMPT_SHORTCUTS,
            payload: {
                conversationId,
                rootPromptShortcut,
            },
        });
    };

    const selectNewConversation = () => {
        const firstConversationEmpty = isEmptyNewConversation(state);

        if (firstConversationEmpty) {
            const firstConversation = getFirstConversation(state);
            // if the first conversation is empty, select it and reset its prompt shortcuts
            const firstConversationId = firstConversation?._id;
            setCurrentConversationId(firstConversationId);
            resetPromptShortcut(firstConversationId, rootPromptShortcut);
        } else {
            // otherwise, create a new conversation, then select it
            const newConversationId = nanoid();
            createConversation(newConversationId);
            setCurrentConversationId(newConversationId);
        }
    };

    return {
        ...state,
        currentConversation: getCurrentConversation(state),

        setMode,
        setReturnMode,
        setTransitionDirection,
        setCurrentConversationId,
        setConversationFetching,
        setConversationError,
        setConversationTitle,
        setConversationStreamingResults,

        loadConversation,
        loadConversationStubs,
        createConversation,
        selectNewConversation,

        appendResult,
        appendPrompt,
        appendDebugInfo,
        removeResult,

        chosenPromptShortcuts: getCurrentConversationChosenPromptShortcuts(state),
        addChosenPromptShortcut,
        removeChosenPromptShortcuts,
        resetToPreviousPromptShortcuts,
        setAdditionalPromptShortcuts,
        setPromptFollowupsStreaming,
    };
};

export default useAiToolStateReducer;
